import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

function timeLeft(iso) {
    if (!iso) return "";
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "Final results";
    const h = Math.floor(ms / 3600000);
    if (h >= 24) return `${Math.floor(h / 24)}d left`;
    if (h >= 1) return `${h}h left`;
    return `${Math.max(1, Math.floor(ms / 60000))}m left`;
}

/** Poll display + voting. `poll` is the server poll_view shape. */
export default function PostActionsPoll({ postType, postId, poll: initial, accent = "#ff4500" }) {
    const { user } = useAuth();
    const [poll, setPoll] = useState(initial);
    const [busy, setBusy] = useState(false);
    if (!poll) return null;

    const showResults = poll.my_vote != null || poll.closed;

    const vote = async (optionId) => {
        if (!user) return toast.error("Log in to vote");
        if (poll.closed || busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/social/poll-vote", { post_type: postType, post_id: postId, option_id: optionId });
            setPoll(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not vote");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-3 space-y-1.5" data-testid={`poll-${postId}`}>
            {poll.options.map((o) => {
                const pct = poll.total ? Math.round((o.votes / poll.total) * 100) : 0;
                const mine = poll.my_vote === o.id;
                return (
                    <button
                        key={o.id}
                        type="button"
                        disabled={showResults}
                        onClick={() => vote(o.id)}
                        data-testid={`poll-option-${o.id}`}
                        className="relative w-full overflow-hidden border border-[#2a2a2a] px-3 py-2 text-left text-sm disabled:cursor-default hover:border-[#3a3a3a]"
                    >
                        {showResults && (
                            <span className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, backgroundColor: `${accent}22` }} />
                        )}
                        <span className="relative flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 text-white">
                                {mine && <Check className="h-3.5 w-3.5" style={{ color: accent }} />}
                                {o.text}
                            </span>
                            {showResults && <span className="text-[#a0a0a0] text-xs font-semibold">{pct}%</span>}
                        </span>
                    </button>
                );
            })}
            <div className="text-[11px] text-[#666]">
                {poll.total} vote{poll.total === 1 ? "" : "s"} · {timeLeft(poll.closes_at)}
            </div>
        </div>
    );
}
